import type { PostList } from "@opinly/backend";

export const BLOG_LANGS = ["nl", "de", "en"] as const;
export type BlogLang = (typeof BLOG_LANGS)[number];

export const BLOG_LANG_LABELS: Record<BlogLang, string> = {
  nl: "Nederlands",
  de: "Deutsch",
  en: "English",
};

type PostLike = {
  slug?: string;
  tags?: { name?: string | null; slug?: string | null }[] | null;
  category?: { name?: string | null; slug?: string | null } | null;
};

const ALIASES: Record<string, BlogLang> = {
  nl: "nl",
  nld: "nl",
  dutch: "nl",
  nederlands: "nl",
  "nl-nl": "nl",
  "nl-be": "nl",
  de: "de",
  deu: "de",
  ger: "de",
  german: "de",
  deutsch: "de",
  "de-de": "de",
  en: "en",
  eng: "en",
  english: "en",
  engels: "en",
  "en-gb": "en",
  "en-us": "en",
};

function normalize(value?: string | null): BlogLang | null {
  if (!value) return null;
  return ALIASES[value.trim().toLowerCase()] ?? null;
}

/**
 * The Opinly content API carries no language field, so the article language is
 * derived from its tags/category (e.g. a "Deutsch" tag) or a slug prefix such
 * as `de-...` / `nl/...`. Anything unmarked is treated as English.
 */
export function detectPostLanguage(post: PostLike): BlogLang {
  for (const tag of post.tags ?? []) {
    const hit = normalize(tag?.slug) ?? normalize(tag?.name);
    if (hit) return hit;
  }
  const cat = normalize(post.category?.slug) ?? normalize(post.category?.name);
  if (cat) return cat;

  const slug = post.slug ?? "";
  const prefix = slug.split(/[/\-_]/)[0];
  return normalize(prefix) ?? "en";
}

export function filterPostsByLanguage<T extends PostLike>(posts: T[], lang?: BlogLang): T[] {
  if (!lang) return posts;
  return posts.filter((post) => detectPostLanguage(post) === lang);
}

export type BlogPostListWithLang = PostList;
